import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { LIST_CALENDAR_RULER, DELETE_CALENDAR_RULER_BY_ID, UPDATE_CALENDAR_RULERS } from '~/services/schedule';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import ScheduleLinkGeneratorDialog from '~/components/shared/ScheduleLinkGeneratorDialog';

interface Schedule {
  id: string;
  title: string;
  description: string;
  isEnabled: boolean;
  meetingType: string;
  duration_of_meeting: number;
  max_users_per_meeting: number;
}

const SchedulesConfiguration: React.FC = () => {
  const [t] = useTranslation('schedule');
  const { agentId } = useParams();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<{ id: string; title: string } | null>(null);
  const confirmDialogRef = useRef<HTMLDialogElement>(null);
  const errorDialogRef = useRef<HTMLDialogElement>(null);
  const [scheduleToDelete, setScheduleToDelete] = useState<string | null>(null);
  
  const [deleteCalendarRulerById, { loading: deleteLoading, error: deleteError }] = useMutation(
      DELETE_CALENDAR_RULER_BY_ID,
    );
  
  const [updateCalendarRuler, { loading: updateLoading, error: updateError }] = useMutation(
      UPDATE_CALENDAR_RULERS,
    );
 
  const { data, loading: fetchLoading, refetch } = useQuery(LIST_CALENDAR_RULER, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
    fetchPolicy: 'network-only',
  });
  
  useEffect(() => {
    if (data?.response?.data && !data.response.hasError) {
      const calendarRulers = data.response.data;
      
      const formattedSchedules = calendarRulers.map((ruler: any) => ({
        id: ruler._id,
        title: ruler.title,
        description: ruler.description,
        isEnabled: ruler.isEnabled,
        meetingType: ruler.meetingType,
        duration_of_meeting: ruler.duration_of_meeting,
        max_users_per_meeting: ruler.max_users_per_meeting
      }));
      
      setSchedules(formattedSchedules);
    }
  }, [data]);

  const toggleScheduleStatus = async (id: string, currentStatus: boolean) => {
    try {
      // Set the current schedule being updated
      setUpdatingId(id);
      
      // Find the current schedule in our local state
      const scheduleToUpdate = schedules.find(schedule => schedule.id === id);
      
      if (!scheduleToUpdate) {
        throw new Error('Schedule not found');
      }
      
      // Create update payload with all required fields
      const updateData = {
        id: id,
        agent_id: agentId,
        title: scheduleToUpdate.title,
        description: scheduleToUpdate.description,
        meetingType: scheduleToUpdate.meetingType,
        duration_of_meeting: scheduleToUpdate.duration_of_meeting,
        meeting_duration_in_mins: scheduleToUpdate.duration_of_meeting,
        max_users_per_meeting: scheduleToUpdate.max_users_per_meeting,
        isEnabled: !currentStatus
      };
      
      // Call the mutation to update the schedule status
      const { data } = await updateCalendarRuler({
        variables: {
          data: updateData
        }
      });
      
      // Check if update was successful
      if (data && !data.update_calendar_rulers.hasError) {
        // Update local state to reflect the new status
        setSchedules(schedules.map(schedule => 
          schedule.id === id 
            ? { ...schedule, isEnabled: !schedule.isEnabled } 
            : schedule
        ));
        console.log('Schedule status updated successfully');
      } else {
        console.error('Error updating schedule status:', data?.update_calendar_rulers.message);
        // Show error message to user
        setErrorMessage(t('update_error') || 'Error updating schedule status');
        setShowErrorDialog(true);
        if (errorDialogRef.current) {
          errorDialogRef.current.showModal();
        }
      }
    } catch (err) {
      console.error('Error updating schedule status:', err);
      // Show error message to user
      setErrorMessage(t('update_error') || 'Error updating schedule status');
      setShowErrorDialog(true);
      if (errorDialogRef.current) {
        errorDialogRef.current.showModal();
      }
    } finally {
      // Clear the updating state regardless of outcome
      setUpdatingId(null);
    }
  };

  const deleteSchedule = async (id: string) => {
    try {
      // Set the current schedule being deleted
      setDeletingId(id);
      
      // Call the mutation to delete the schedule
      const { data } = await deleteCalendarRulerById({
        variables: {
          data: {
            id: id
          }
        }
      });
      
      // Check if deletion was successful
      if (data && !data.delete_calendar_ruler_by_id.hasError) {
        // Update local state to remove the deleted schedule
        setSchedules(schedules.filter(schedule => schedule.id !== id));
        console.log('Schedule deleted successfully');
        
        // Optionally show a success message
        // You could use a toast notification here
      } else {
        console.error('Error deleting schedule:', data?.delete_calendar_ruler_by_id.message);
        // Show error message to user
        setErrorMessage(t('delete_error'));
        setShowErrorDialog(true);
        if (errorDialogRef.current) {
          errorDialogRef.current.showModal();
        }
      }
    } catch (err) {
      console.error('Error deleting schedule:', err);
      // Show error message to user
      setErrorMessage(t('delete_error'));
      setShowErrorDialog(true);
      if (errorDialogRef.current) {
        errorDialogRef.current.showModal();
      }
    } finally {
      // Clear the deleting state regardless of outcome
      setDeletingId(null);
      setScheduleToDelete(null);
    }
  };

  const handleGenerateScheduleLink = (scheduleId: string, scheduleTitle: string) => {
    console.log('SchedulesConfiguration - handleGenerateScheduleLink called with:', { scheduleId, scheduleTitle });
    setSelectedSchedule({ id: scheduleId, title: scheduleTitle });
    setIsLinkDialogOpen(true);
  };

  const handleCreateGeneralLink = () => {
    // Open the phone dialog instead of directly generating link
    setSelectedSchedule(null); // Clear any selected schedule for general link
    setIsLinkDialogOpen(true);
  };

  const handlePreviewAllSchedules = () => {
    // Preview all schedules functionality
    console.log('Preview all schedules for agent:', agentId);
    // Navigate to preview scheduler with agent ID parameter
    window.open(`/calendar/preview?agentId=${agentId}`, '_blank');
  };

  const handleLinkGenerated = (link: string) => {
    console.log('Schedule link generated:', link);
    setSuccessMessage(t('link_generated'));
    
    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">{t('manage_schedules')}</h2>
        <div className="flex gap-2">
          {schedules.length > 0 && (
            <>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={handleCreateGeneralLink}
                title="Create general link for all schedules"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                {t('create_general_link')}
              </button>
              <button 
                className="btn btn-sm btn-accent"
                onClick={handlePreviewAllSchedules}
                title="Preview all schedules"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {t('preview_all_schedules')}
              </button>
            </>
          )}
          <button 
            className="btn btn-sm btn-outline" 
            onClick={() => {
              // Refetch data from the server
              if (refetch) refetch();
            }}
            disabled={fetchLoading}
          >
            {fetchLoading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            <span className="ml-1">{t('refresh')}</span>
          </button>
        </div>
      </div>
      
      {fetchLoading ? (
        // Loading skeleton
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>{t('schedule_name')}</th>
                <th>{t('meeting_type')}</th>
                <th>{t('meeting_duration')}</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3].map((item) => (
                <tr key={item} className="animate-pulse">
                  <td><div className="h-4 bg-gray-200 rounded w-3/4"></div></td>
                  <td><div className="h-4 bg-gray-200 rounded w-1/2"></div></td>
                  <td><div className="h-4 bg-gray-200 rounded w-1/3"></div></td>
                  <td><div className="h-4 bg-gray-200 rounded w-1/2"></div></td>
                  <td><div className="flex gap-2">
                    <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : schedules.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>{t('schedule_name')}</th>
                <th>{t('meeting_type')}</th>
                <th>{t('meeting_duration')}</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(schedule => (
                <tr key={schedule.id}>
                  <td>{schedule.title}</td>
                  <td>
                    {schedule.meetingType === 'VIRTUAL' ? 
                      t('virtual_meeting') : t('in_person_meeting')
                    }
                  </td>
                  <td>{schedule.duration_of_meeting} {t('minutes')}</td>
                  <td>
                    <div className="form-control">
                      <label className="cursor-pointer label justify-start gap-2">
                        <input 
                          type="checkbox" 
                          className="toggle toggle-primary" 
                          checked={schedule.isEnabled}
                          onChange={() => toggleScheduleStatus(schedule.id, schedule.isEnabled)}
                          disabled={updatingId === schedule.id}
                        />
                        <span className="label-text">
                          {updatingId === schedule.id ? (
                            <span className="loading loading-spinner loading-xs mr-1"></span>
                          ) : null}
                          {schedule.isEnabled ? t('active') : t('inactive')}
                        </span>
                      </label>
                    </div>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <Link 
                        to={`../edit/${schedule.id}`} 
                        className="btn btn-sm btn-ghost" 
                        title={t('edit')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Link>
                      <button 
                        className="btn btn-sm btn-ghost text-primary" 
                        onClick={() => handleGenerateScheduleLink(schedule.id, schedule.title)}
                        title={t('generate_link')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </button>
                      <button 
                        className="btn btn-sm btn-ghost text-error" 
                        onClick={() => {
                          // Open confirmation dialog before deleting
                          setScheduleToDelete(schedule.id);
                          if (confirmDialogRef.current) {
                            confirmDialogRef.current.showModal();
                          }
                        }}
                        title={t('delete')}
                        disabled={deleteLoading && deletingId === schedule.id} // Disable during deletion process
                      >
                        {deleteLoading && deletingId === schedule.id ? (
                          <span className="loading loading-spinner loading-xs"></span>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center p-10 bg-base-200 rounded-lg">
          <p className="text-lg">{t('no_schedules')}</p>
        </div>
      )}
      
      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success shadow-lg mb-6">
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{successMessage}</span>
          </div>
          <div className="flex-none">
            <button 
              onClick={() => setSuccessMessage(null)} 
              className="btn btn-sm btn-ghost"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {deleteError && (
        <div className="alert alert-error mt-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{t('delete_error')}</span>
        </div>
      )}
      
      {updateError && (
        <div className="alert alert-error mt-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{t('update_error') || 'Error updating schedule status'}</span>
        </div>
      )}
      
      {/* Confirmation Dialog */}
      <dialog id="delete_confirm_dialog" className="modal modal-bottom sm:modal-middle" ref={confirmDialogRef}>
        <form method="dialog" className="modal-box">
          <h3 className="font-bold text-lg">{t('confirm_delete')}</h3>
          <p className="py-4">{t('confirm_delete')}</p>
          <div className="modal-action">
            <button className="btn btn-secondary" onClick={() => confirmDialogRef.current?.close()}>{t('cancel')}</button>
            <button 
              className="btn btn-primary rounded-full text-white" 
              onClick={() => {
                if (scheduleToDelete) {
                  deleteSchedule(scheduleToDelete);
                }
                confirmDialogRef.current?.close();
              }}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : null}
              {t('delete')}
            </button>
          </div>
        </form>
      </dialog>
      
      {/* Error Dialog */}
      <dialog id="error_dialog" className="modal modal-bottom sm:modal-middle" ref={errorDialogRef}>
        <form method="dialog" className="modal-box">
          <h3 className="font-bold text-lg text-error">{t('error')}</h3>
          <p className="py-4">{errorMessage}</p>
          <div className="modal-action">
            <button className="btn btn-primary rounded-full" onClick={() => errorDialogRef.current?.close()}>OK</button>
          </div>
        </form>
      </dialog>
      
      {/* Schedule Link Generator Dialog */}
      <ScheduleLinkGeneratorDialog
        isOpen={isLinkDialogOpen}
        onClose={() => {
          setIsLinkDialogOpen(false);
          setSelectedSchedule(null);
        }}
        agentId={agentId!}
        calendar_rulers_id={selectedSchedule?.id}
        scheduleTitle={selectedSchedule?.title}
        onLinkGenerated={handleLinkGenerated}
      />
    </div>
  );
};

export default SchedulesConfiguration;
